import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import Styles from './vm-video-hub-related.scss';
import '../vm-video-hub-card/vm-video-hub-card';

import { DESKTOP_BREAKPOINT_QUERY } from 'Utils/viewport';

/**
 * Responsive wrapper for a highlighted channel's related-content section.
 *
 * On desktop it wraps the slotted related content in a `vm-video-hub-card` (channel
 * logo, name, and "Visit Hub" CTA). On mobile it drops the card chrome and renders the
 * related content on its own — the hub card itself is surfaced separately on mobile.
 *
 * @slot - Default. The related-content list to render inside (desktop) or below (mobile) the hub card.
 */
@customElement('vm-video-hub-related')
export class VmVideoHubRelated extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'channel-name' }) channelName = '';
    @property({ type: String, attribute: 'channel-image-src' }) channelImageSrc = '';
    @property({ type: String, attribute: 'channel-image-alt' }) channelImageAlt = '';
    @property({ type: String, attribute: 'channel-url' }) channelUrl = '';
    /** The qualifying channel's slug, forwarded to the hub card for its Mixpanel events. */
    @property({ type: String, attribute: 'channel-slug' }) channelSlug = '';

    private _mediaQuery = window.matchMedia(DESKTOP_BREAKPOINT_QUERY);

    @state() private _isDesktop = this._mediaQuery.matches;

    private _onBreakpointChange = (e: MediaQueryListEvent) => {
        this._isDesktop = e.matches;
    };

    override connectedCallback() {
        super.connectedCallback();
        this._mediaQuery.addEventListener('change', this._onBreakpointChange);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this._mediaQuery.removeEventListener('change', this._onBreakpointChange);
    }

    override render() {
        return this._isDesktop
            ? html`
                  <vm-video-hub-card
                      channel-name=${this.channelName}
                      channel-image-src=${this.channelImageSrc}
                      channel-image-alt=${this.channelImageAlt}
                      channel-url=${this.channelUrl}
                      channel-slug=${this.channelSlug}
                  >
                      <slot slot="description"></slot>
                  </vm-video-hub-card>
              `
            : html`<slot></slot>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-hub-related': VmVideoHubRelated;
    }
}
